/**
 * The bottom "Show all" bar for index grids — the sole "Show all" affordance
 * (the toolbar carries search only). `useIndexGridShell` wires it into
 * `tableProps.renderCapFooter`, so the primitive renders it inside the scroll
 * area right after the last row, but ONLY while the render cap is actively
 * truncating (see `EditableTable`'s `renderCapFooter`) — i.e. exactly where
 * the cap bites, so lifting it there has a clear referent.
 *
 * The primitive renders this inside a full-width `<tfoot>` cell, so the outer
 * bar spans the full scrollable table width (a clean separator line); the inner
 * content is `w-fit` + `sticky left-0`, so the caption and button stay pinned to
 * the viewport's left edge and remain visible even when the grid is scrolled
 * horizontally.
 */
import { useTranslation } from '@/hooks/useTranslation';
import { IndexGridShowAllButton } from './IndexGridShowAllButton';

export interface IndexGridCapFooterProps {
  /** Post-column-filter total row count — the "of N" and the button's count. */
  total: number;
  /** How many rows are actually rendered while capped (the render cap). */
  shown: number;
  /** Lift the render cap — typically `shell.setShowAll(true)`. */
  onShowAll: () => void;
}

export function IndexGridCapFooter({ total, shown, onShowAll }: IndexGridCapFooterProps) {
  const { t } = useTranslation('spreadsheet');
  return (
    <div className="border-t border-border bg-muted/30">
      <div className="sticky left-0 w-fit flex items-center gap-3 px-4 py-1.5">
        <span className="text-xs text-muted-foreground">
          {t('indexGrid.showingCapped', { shown, total })}
        </span>
        <IndexGridShowAllButton total={total} onClick={onShowAll} />
      </div>
    </div>
  );
}
